export const safeGetSelectedCellId = (selection, structure) => {
  const id =
    selection.cells.length > 0
      ? selection.cells[0].id
      : selection.columns.length > 0
        ? structure.cells[0][selection.columns[0]].id
        : selection.rows.length > 0
          ? structure.cells[selection.rows[0]][0].id
          : null;
  //console.log("Getting selected id safely", { id, selection, structure });
  return id;
};

export const isACellSelected = (selection) => {
  return selection.cells?.length > 0;
};

export const isSpacerSelected = (selection, structure) => {
  // Check if the selected column or row is a spacer
  if (selection.columns.length > 0) {
    const col = structure.columns[selection.columns[0]];
    return col?.length > 0 ? col[0] === "s" : false;
  }
  if (selection.rows.length > 0) {
    const row = structure.rows[selection.rows[0]];
    return row?.length > 0 ? row[0] === "s" : false;
  }
  return false;
};

export const isAnySelected = (selection) => {
  return (
    selection.cells?.length > 0 ||
    selection.columns?.length > 0 ||
    selection.rows?.length > 0 ||
    selection.borders?.length > 0 ||
    selection.slot
  );
};

export const isAllCellsSelected = (selection, structure) => {
  const totalCellCount = structure.columns.length * structure.rows.length;
  const selectedCellsByColumns = selection.columns?.length ? selection.columns.length * structure.rows.length : 0;
  const selectedCellsByRows = selection.rows?.length ? selection.rows.length * structure.columns.length : 0;
  return (
    selection.cells?.length === totalCellCount ||
    selectedCellsByColumns === totalCellCount ||
    selectedCellsByRows === totalCellCount
  );
};

export const getAllCellsInCol = (cells, columnIndex, rowCount) => {
  const cs = [];
  for (let i = 0; i < rowCount; i++) {
    cs.push(cells[i][columnIndex]);
  }
  return cs;
};

export const getAllCellsInRow = (cells, rowIndex, colCount) => {
  const cs = [];
  for (let i = 0; i < colCount; i++) {
    cs.push(cells[rowIndex][i]);
  }
  return cs;
};

export const getAllCellsInRect = (cells, colStart, rowStart, colEnd, rowEnd) => {
  const x1 = colStart,
    y1 = rowStart;
  const x2 = colEnd ?? colStart + 1,
    y2 = rowEnd ?? rowStart + 1;
  const cs = [];

  for (let i = x1; i < x2; i++) {
    for (let j = y1; j < y2; j++) {
      cs.push(cells[i][j].id);
    }
  }

  return cs;
};

export const getAllCellsInCols = (cells, columns, rowCount) => {
  const cs = [];
  for (let i = 0; i < columns.length; i++) {
    cs.push(...getAllCellsInCol(cells, columns[i], rowCount));
  }
  return cs;
};

export const getAllCellsInRows = (cells, rows, columnCount) => {
  const cs = [];
  for (let i = 0; i < rows.length; i++) {
    cs.push(...getAllCellsInRow(cells, rows[i], columnCount));
  }
  return cs;
};

export const getSelectedCells = (state) => {
  const selection = state.selection;
  const gridd = state.gridd?.present ?? { structure: state.structure };
  return selection.columns?.length > 0 && selection.columns[0] !== undefined
    ? getAllCellsInCols(gridd.structure.cells, selection.columns, gridd.structure.rows.length)
    : selection.rows?.length > 0 && selection.rows[0] !== undefined
      ? getAllCellsInRows(gridd.structure.cells, selection.rows, gridd.structure.columns.length)
      : (selection.cells ?? []);
};

import { batchGroupBy } from "./batchGroupBy";
export const onSelection = (dispatch, action, state, payload) => {
  const cells = getSelectedCells(state);

  batchGroupBy.start();
  cells.forEach((cell) => {
    const id = cell.id ?? state.structure.cells[cell.y][cell.x]?.id;
    if (payload && payload.sizeMap && payload.sizeMap[cell.id]) {
      dispatch(action({ id, size: payload.sizeMap[cell.id] }));
    } else {
      dispatch(action({ id, ...payload }));
    }
  });
  batchGroupBy.end();
};

export const onSelectionByCoordinates = (dispatch, action, state, payload) => {
  const cells = getSelectedCells(state);
  const structure = state.structure ?? state.gridd.present.structure;

  batchGroupBy.start();
  cells.forEach((cell) => {
    let x = 0,
      y = 0;
    for (let i = 0; i < structure.cells.length; i++) {
      for (let j = 0; j < structure.cells[i].length; j++) {
        if (structure.cells[i][j].id === cell.id) {
          x = i;
          y = j;
          break;
        }
      }
    }

    dispatch(action({ id: cell.id, x, y, ...payload }));
  });
  batchGroupBy.end();
};

export const isMouseClickedOnBorder = (e, cellElement) => {
  const boundingBox = cellElement.getBoundingClientRect();
  const sensitivity = 6;
  const xDifToLeft = Math.abs(boundingBox.left - e.clientX);
  const isOnLeftBorder = xDifToLeft < sensitivity;
  const xDifToRight = Math.abs(boundingBox.right - e.clientX);
  const isOnRightBorder = xDifToRight < sensitivity;
  const yDifToTop = Math.abs(boundingBox.top - e.clientY);
  const isOnTopBorder = yDifToTop < sensitivity;
  const yDifToBot = Math.abs(boundingBox.bottom - e.clientY);
  const isOnBotBorder = yDifToBot < sensitivity;
  const isOnBorder = isOnLeftBorder || isOnRightBorder || isOnTopBorder || isOnBotBorder;
  return { isOnBorder, isOnLeftBorder, isOnRightBorder, isOnTopBorder, isOnBotBorder };
};

export const findBorderOnClick = (isMouseClickedOnBorderResult, borders, cell) => {
  const { isOnBorder, isOnLeftBorder, isOnRightBorder, isOnTopBorder, isOnBotBorder } = isMouseClickedOnBorderResult;
  if (isOnBorder) {
    let firstBorderFound = isOnLeftBorder
      ? borders.find(
          (b) => b.isVertical && b.index === cell.y && b.from <= cell.x && (b.to ?? Number.MAX_VALUE) > cell.x
        )
      : null;

    if (!firstBorderFound) {
      firstBorderFound = isOnRightBorder
        ? borders.find(
            (b) => b.isVertical && b.index === cell.y + 1 && b.from <= cell.x && (b.to ?? Number.MAX_VALUE) > cell.x
          )
        : null;

      if (!firstBorderFound) {
        firstBorderFound = isOnTopBorder
          ? borders.find(
              (b) => !b.isVertical && b.index === cell.x && b.from <= cell.y && (b.to ?? Number.MAX_VALUE) > cell.y
            )
          : null;

        if (!firstBorderFound) {
          firstBorderFound = isOnBotBorder
            ? borders.find(
                (b) =>
                  !b.isVertical && b.index === cell.x + 1 && b.from <= cell.y && (b.to ?? Number.MAX_VALUE) > cell.y
              )
            : null;
        }
      }
    }

    if (firstBorderFound) {
      const result = {
        id: firstBorderFound?.id,
        isVertical: isOnLeftBorder || isOnRightBorder,
        index: isOnLeftBorder ? cell.y : isOnRightBorder ? cell.y + 1 : isOnTopBorder ? cell.x : cell.x + 1,
      };
      //console.log("border found", firstBorderFound);
      return result;
    }

    //console.log("border not found", { isMouseClickedOnBorderResult, borders, cell });
  }

  return null;
};
